import {
  CaretDown,
  CircleNotch,
  Sparkle,
  Warning,
  X,
} from '@phosphor-icons/react';
import { Badge, Button } from '@fragments-sdk/ui';
import {
  findingTrustSummary,
  parseFindingEvidence,
  parseFindingFix,
  stringifyJsonPreview,
} from '../finding-detail-model';
import {
  canonicalSeverity,
  findingSourceLabel,
  governanceLabel,
  prImpactLabel,
  prReviewStatusLabel,
  severityLabel,
} from '../findings-display-model';
import type {
  PrActionState,
  PrDecisionDetail,
  PrDecisionRow,
} from '../findings-types';
import { getPrDecisionGate } from './pr-workbench-model';
import {
  hasGovernanceAutomation,
  type PlanId,
} from '../../../../../convex/billing/plans';
import s from '../findings.module.scss';

type PrDecisionCardProps = {
  decision: PrDecisionRow;
  detail?: PrDecisionDetail | null;
  selected: boolean;
  loadingDetail: boolean;
  plan: PlanId;
  readOnly: boolean;
  quotaReached: boolean;
  actionState: PrActionState;
  onSelect: () => void;
  onCreateFix: (detail: PrDecisionDetail) => void;
  onPostComments: (detail: PrDecisionDetail) => void;
  onApprove: (detail: PrDecisionDetail) => void;
  onRequestFix: (detail: PrDecisionDetail) => void;
};

export function PrDecisionCard({
  decision,
  detail,
  selected,
  loadingDetail,
  plan,
  readOnly,
  quotaReached,
  actionState,
  onSelect,
  onCreateFix,
  onPostComments,
  onApprove,
  onRequestFix,
}: PrDecisionCardProps) {
  const gate = getPrDecisionGate(decision);
  const Icon = gate === 'blocking' ? X : Warning;

  return (
    <article className={s.prDecisionCard} data-gate={gate}>
      <button type="button" className={s.prDecisionHeader} onClick={onSelect}>
        <span className={s.prDecisionIcon} data-gate={gate}>
          <Icon size={15} />
        </span>
        <span className={s.prDecisionTitleBlock}>
          <strong>{decision.title}</strong>
          <span>
            {decision.ruleIds[0] ?? 'Finding'} ·{' '}
            {decision.topFiles[0]?.filePath ?? decision.contractLabel}
          </span>
        </span>
        <Badge
          variant="label"
          size="sm"
          className={s.prGovernanceBadge}
          data-actionability={decision.actionability}
        >
          {governanceLabel(decision.actionability)}
        </Badge>
        <CaretDown
          size={16}
          className={s.prDecisionChevron}
          data-open={selected || undefined}
        />
      </button>

      {selected && (
        <PrDecisionExpanded
          decision={decision}
          detail={detail}
          loading={loadingDetail}
          plan={plan}
          readOnly={readOnly}
          quotaReached={quotaReached}
          actionState={actionState}
          onCreateFix={onCreateFix}
          onPostComments={onPostComments}
          onApprove={onApprove}
          onRequestFix={onRequestFix}
        />
      )}
    </article>
  );
}

function PrDecisionExpanded({
  decision,
  detail,
  loading,
  plan,
  readOnly,
  quotaReached,
  actionState,
  onCreateFix,
  onPostComments,
  onApprove,
  onRequestFix,
}: {
  decision: PrDecisionRow;
  detail?: PrDecisionDetail | null;
  loading: boolean;
  plan: PlanId;
  readOnly: boolean;
  quotaReached: boolean;
  actionState: PrActionState;
  onCreateFix: (detail: PrDecisionDetail) => void;
  onPostComments: (detail: PrDecisionDetail) => void;
  onApprove: (detail: PrDecisionDetail) => void;
  onRequestFix: (detail: PrDecisionDetail) => void;
}) {
  if (loading) {
    return (
      <div className={s.prDecisionExpanded} aria-busy>
        <CircleNotch size={16} className={s.spinIcon} />
        Loading finding details...
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={s.prDecisionExpanded}>
        Detail was not found for this finding group.
      </div>
    );
  }

  const representative = detail.representativeFinding;
  const occurrenceEvidenceJson = detail.occurrences.find(
    (occurrence) => occurrence.evidenceJson,
  )?.evidenceJson;
  const occurrenceFixJson = detail.occurrences.find(
    (occurrence) => occurrence.fixJson,
  )?.fixJson;
  const evidence = parseFindingEvidence(
    representative?.evidenceJson ?? occurrenceEvidenceJson,
  );
  const fix = parseFindingFix(representative?.fixJson ?? occurrenceFixJson);
  const trust = findingTrustSummary({
    evidenceJson: representative?.evidenceJson ?? occurrenceEvidenceJson,
    fixJson: representative?.fixJson ?? occurrenceFixJson,
    source: representative?.source ?? detail.decision.source,
    filePath: representative?.filePath,
    commitSha: representative?.commitSha,
    sourceVerified: representative?.sourceVerified,
    sourceVerificationMethod: representative?.sourceVerificationMethod,
  });
  const currentValue =
    detail.suggestedFix?.from ??
    representative?.rawValue ??
    decision.primaryValue ??
    'Not recorded';
  const targetValue =
    detail.suggestedFix?.to ??
    representative?.suggestedToken ??
    decision.suggestedTarget ??
    'Review required';
  const canAutofix =
    !readOnly &&
    hasGovernanceAutomation(plan) &&
    detail.decision.safeFixFindingIds.length > 0 &&
    detail.suggestedFix?.canAutofix === true &&
    !quotaReached;
  const actionBusy = actionState.status === 'running';
  const patchPreview = fix?.patch ? stringifyJsonPreview(fix.patch) : null;

  return (
    <div className={s.prDecisionExpanded}>
      <div className={s.prDecisionBadges}>
        <Badge
          variant="label"
          size="sm"
          className={s.severityBadge}
          data-severity={canonicalSeverity(decision.severity)}
        >
          {severityLabel(decision.severity)}
        </Badge>
        <Badge variant="label" size="sm">
          {findingSourceLabel(decision.source)}
        </Badge>
        <Badge variant="label" size="sm">
          {prImpactLabel(decision)}
        </Badge>
        <Badge variant="label" size="sm">
          {prReviewStatusLabel(decision.reviewStatus)}
        </Badge>
        <Badge variant="label" size="sm">
          {trust.label}
        </Badge>
      </div>

      <div className={s.prValueCompare}>
        <div>
          <span>Design-system target</span>
          <strong>{targetValue}</strong>
          <small>{detail.contract.label}</small>
        </div>
        <span aria-hidden>→</span>
        <div>
          <span>In code</span>
          <strong>{currentValue}</strong>
          <small>
            {representative?.filePath ??
              detail.occurrences[0]?.filePath ??
              'Source not recorded'}
          </small>
        </div>
      </div>

      <div className={s.prEvidenceGrid}>
        <section>
          <h3>Evidence</h3>
          {evidence.length > 0 ? (
            <div className={s.prEvidenceList}>
              {evidence.slice(0, 3).map((item, index) => (
                <div
                  key={`${item.factId ?? item.label ?? 'evidence'}-${index}`}
                >
                  <strong>
                    {item.label ?? item.factId ?? `Evidence ${index + 1}`}
                  </strong>
                  <span>
                    {item.filePath ?? representative?.filePath ?? 'Source'}{' '}
                    {item.line != null ? `:${item.line}` : ''}
                  </span>
                  {item.excerpt && <code>{item.excerpt}</code>}
                </div>
              ))}
            </div>
          ) : (
            <p>{trust.detail}</p>
          )}
        </section>

        <section>
          <h3>In this PR</h3>
          <div className={s.prOccurrenceList}>
            {detail.occurrences.slice(0, 8).map((occurrence) => (
              <span
                key={`${occurrence.filePath}:${occurrence.line}:${occurrence.column}`}
              >
                {occurrence.filePath}:{occurrence.line} · {occurrence.impact}
              </span>
            ))}
          </div>
        </section>
      </div>

      {patchPreview && (
        <section className={s.prPatchPreview}>
          <h3>Preview diff</h3>
          <pre>{patchPreview}</pre>
        </section>
      )}

      <div className={s.prFixCallout}>
        <span>
          <Sparkle size={16} />
          Apply this fix and re-run checks.
        </span>
        <span className={s.prFixActions}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!detail.suggestedFix}
          >
            Preview diff
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canAutofix || actionBusy}
            onClick={() => onCreateFix(detail)}
          >
            {actionBusy ? 'Working' : 'Apply autofix'}
          </Button>
        </span>
      </div>

      <div className={s.prDecisionActions}>
        <Button
          type="button"
          variant="outlined"
          size="sm"
          disabled={readOnly || actionBusy}
          onClick={() => onApprove(detail)}
        >
          Approve
        </Button>
        <Button
          type="button"
          variant="outlined"
          size="sm"
          disabled={readOnly || actionBusy}
          onClick={() => onRequestFix(detail)}
        >
          Request fix
        </Button>
        <Button
          type="button"
          variant="outlined"
          size="sm"
          disabled={
            readOnly ||
            actionBusy ||
            detail.decision.openFindingIds.length === 0
          }
          onClick={() => onPostComments(detail)}
        >
          Post comments
        </Button>
      </div>
    </div>
  );
}
